export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
export const LOAD_ERROR_MESSAGE = 'We could not load this right now. Please try again.';
export const SAVE_ERROR_MESSAGE = 'We could not save your changes. Please try again.';
export const CONNECTION_ERROR_MESSAGE = 'We lost the connection. Please try again.';

/**
 * Return copy that is safe to render in product UI.
 *
 * Error objects often contain provider names, HTTP details, stack fragments,
 * database messages, identifiers, or backend response bodies. Those details
 * belong in logs, never in the interface. Callers should log the original
 * error and pass a contextual, product-written fallback here.
 */
export function userFacingError(_error, fallback = GENERIC_ERROR_MESSAGE) {
  return typeof fallback === 'string' && fallback.trim()
    ? fallback
    : GENERIC_ERROR_MESSAGE;
}

/**
 * Safe copy for transport failures. This intentionally uses only the status
 * number; response text and response bodies are diagnostic data.
 */
export function apiErrorMessage(status, code = null) {
  if (code === 'INVITE_EXPIRED') return 'This invitation has expired.';
  if (code === 'BILLING_OWNER_REQUIRED') return 'Only the organization owner can change billing settings.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'We could not find what you requested.';
  if (status === 409) return 'This changed somewhere else. Refresh and try again.';
  if (status === 429) return 'Too many requests were made. Please wait a moment and try again.';
  return GENERIC_ERROR_MESSAGE;
}

/**
 * Safe summary for failed background jobs and workflow nodes.
 */
export function failedTaskMessage(label = 'This task') {
  const safeLabel = typeof label === 'string' && label.trim() ? label.trim() : 'This task';
  return `${safeLabel} could not be completed. Please try again.`;
}

const TECHNICAL_ERROR_KEY = /^(?:error|errors|error_message|failure_reason|exception|stack|stacktrace|traceback)$/i;

/**
 * Remove error-shaped fields before structured API/job output is rendered or
 * offered as a UI download. Non-error result data is preserved.
 */
export function sanitizeForDisplay(value, seen = new WeakSet()) {
  if (value === null || value === undefined) return value;
  if (value instanceof Error) return GENERIC_ERROR_MESSAGE;
  if (Array.isArray(value)) return value.map((item) => sanitizeForDisplay(item, seen));
  if (typeof value !== 'object') return value;
  if (seen.has(value)) return '[Unavailable]';
  seen.add(value);

  const sanitized = {};
  Object.entries(value).forEach(([key, child]) => {
    sanitized[key] = TECHNICAL_ERROR_KEY.test(key)
      ? GENERIC_ERROR_MESSAGE
      : sanitizeForDisplay(child, seen);
  });
  return sanitized;
}
